import { Check } from "lucide-react";
import { ButtonLink, Card, Table, Th, Td } from "../../components/ui";
import { RevenueCalculator } from "../../components/marketing/RevenueCalculator";
import { DEFAULT_INFRA_PRICE_TABLE } from "@dbindex/pricing";
import { formatMoney, formatNumber } from "../../lib/format";

export function Pricing() {
  const t = DEFAULT_INFRA_PRICE_TABLE;
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <h1 className="text-3xl font-semibold tracking-tight">Pricing</h1>
      <p className="mt-2 max-w-2xl text-fg-2">Usage-based on both sides of the network. Providers set their own query price; consumers pay only for queries they run.</p>
      <div className="mt-10 grid gap-4 md:grid-cols-3">
        {[
          { h: "Data providers", p: "20% marketplace fee", d: "on query revenue", items: ["Free to connect and classify", "Hosting estimated before activation", "Hosting allocated against earnings", "Payouts from available balance"] },
          { h: "AI consumers", p: "Provider price", d: "per successful query", items: ["Prepaid credits or invoiced billing", "Monthly budgets and per-query price caps", "Scoped API keys and service accounts", "Provenance and licence on every response"] },
          { h: "Enterprise", p: "Contact us", d: "reserved capacity", items: ["Live origin query mode", "Dedicated serving shards", "Two-person approval (roadmap)", "SSO / SCIM (roadmap)"] },
        ].map((c) => (
          <Card key={c.h} className="p-6">
            <h2 className="text-sm font-semibold">{c.h}</h2>
            <div className="mt-3 text-2xl font-semibold">{c.p}</div>
            <div className="text-xs text-fg-3">{c.d}</div>
            <ul className="mt-5 space-y-2 text-sm text-fg-2">
              {c.items.map((i) => (
                <li key={i} className="flex gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
                  {i}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
      <h2 className="mt-16 text-xl font-semibold">Estimate your economics</h2>
      <div className="mt-6">
        <RevenueCalculator />
      </div>
      <h2 className="mt-16 text-xl font-semibold">Infrastructure price table</h2>
      <p className="mt-2 max-w-2xl text-sm text-fg-2">
        Hosting estimates use a versioned price table (version <code className="font-mono">{t.version}</code>) that can be updated without a deployment. Source: {t.source}.
      </p>
      <Card className="mt-4">
        <Table>
          <thead>
            <tr>
              <Th>Product</Th>
              <Th>Unit</Th>
              <Th align="right">Price</Th>
              <Th align="right">Included</Th>
              <Th>Effective from</Th>
            </tr>
          </thead>
          <tbody>
            {t.rows.map((r) => (
              <tr key={`${r.product}-${r.unit}-${r.tier ?? ""}`}>
                <Td mono>{r.product}</Td>
                <Td className="text-fg-2">
                  per {formatNumber(r.perUnits)} {r.unit}
                </Td>
                <Td align="right">{formatMoney(r.unitPriceMicros, { precision: 6 })}</Td>
                <Td align="right">{r.includedAllowance ? formatNumber(r.includedAllowance, { compact: true }) : "—"}</Td>
                <Td className="text-fg-2">{r.effectiveFrom}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <div className="mt-12 flex gap-3">
        <ButtonLink to="/signup" variant="primary">
          Connect a Database
        </ButtonLink>
        <ButtonLink to="/docs" variant="outline">
          Query DBIndex
        </ButtonLink>
      </div>
    </div>
  );
}
